import { useEffect, useState } from "react";
import client from "../api/client";
import { useAuth } from "../context/AuthContext";

const blank = {
  fullName: "",
  email: "",
  password: "",
  roleId: "",
  companyId: "",
  isActive: true,
};

const styles = {
  page: { padding: 0, fontSize: 11 },
  card: {
    border: "none",
    borderRadius: 12,
    boxShadow: "0 2px 10px rgba(0,0,0,.07)",
    overflow: "hidden",
    background: "#fff",
  },
  header: {
    background: "linear-gradient(90deg, #22052D, #CCB3D1)",
    color: "#fff",
    padding: "8px 12px",
    fontSize: 12,
    fontWeight: 700,
  },
  body: { padding: 12 },
  label: {
    fontSize: 10,
    fontWeight: 700,
    color: "#495057",
    marginBottom: 3,
  },
  input: {
    fontSize: 11,
    height: 30,
    borderRadius: 7,
  },
  btn: {
    fontSize: 11,
    height: 31,
    borderRadius: 7,
    fontWeight: 600,
  },
  table: { fontSize: 11, marginBottom: 0 },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    whiteSpace: "nowrap",
    color: "#fff",
  },
  td: {
    fontSize: 11,
    padding: "6px 8px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  badge: {
    fontSize: 10,
    padding: "4px 7px",
    borderRadius: 20,
    fontWeight: 600,
  },
};

export default function AdminUsersPage() {
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    ...blank,
    companyId: user?.companyId || "",
  });
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const [r, c, u] = await Promise.all([
      client.get("/security/roles"),
      client.get("/companies"),
      client.get("/security/users"),
    ]);

    const roleRows = r.data || [];
    setRoles(roleRows);
    setCompanies(c.data || []);
    setUsers(u.data || []);

    if (!form.roleId && roleRows.length) {
      setForm((p) => ({
        ...p,
        roleId: String(roleRows[0].id || roleRows[0].Id),
      }));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setMessage("");

    if (
      !form.fullName.trim() ||
      !form.email.trim() ||
      !form.password.trim() ||
      !form.roleId
    ) {
      setMessage("Full name, email, password and role are required.");
      return;
    }

    if (form.password.length < 8) {
      setMessage("Password must be at least 8 characters.");
      return;
    }

    setLoading(true);

    try {
      await client.post("/security/users", {
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        password: form.password,
        roleId: Number(form.roleId),
        companyId: form.companyId ? Number(form.companyId) : null,
        isActive: !!form.isActive,
      });

      setMessage("User registered successfully.");
      setForm({
        ...blank,
        roleId: form.roleId,
        companyId: form.companyId || user?.companyId || "",
      });

      await load();
    } catch (err) {
      setMessage(err.response?.data || "Failed to register user.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid" style={styles.page}>
      <div className="row g-2">
        <div className="col-lg-4">
          <div style={{ ...styles.card, height: "100%" }}>
            <div style={styles.header}>
              <i className="bi bi-person-plus me-2"></i>
              Admin Register User
            </div>

            <form className="row g-2" style={styles.body} onSubmit={save}>
              {message && (
                <div
                  className={`alert mb-1 ${
                    message.includes("success")
                      ? "alert-success"
                      : "alert-warning"
                  }`}
                  style={{ fontSize: 11, padding: "7px 10px" }}
                >
                  {message}
                </div>
              )}

              <div className="col-12">
                <label style={styles.label}>Full Name</label>
                <input
                  className="form-control form-control-sm"
                  style={styles.input}
                  value={form.fullName}
                  onChange={(e) =>
                    setForm({ ...form, fullName: e.target.value })
                  }
                />
              </div>

              <div className="col-12">
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  className="form-control form-control-sm"
                  style={styles.input}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>

              <div className="col-12">
                <label style={styles.label}>Temporary Password</label>
                <input
                  type="password"
                  className="form-control form-control-sm"
                  style={styles.input}
                  value={form.password}
                  onChange={(e) =>
                    setForm({ ...form, password: e.target.value })
                  }
                />
              </div>

              <div className="col-md-6">
                <label style={styles.label}>Role</label>
                <select
                  className="form-select form-select-sm"
                  style={styles.input}
                  value={form.roleId}
                  onChange={(e) => setForm({ ...form, roleId: e.target.value })}
                >
                  {roles.map((r) => (
                    <option key={r.id || r.Id} value={r.id || r.Id}>
                      {r.roleName || r.RoleName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6">
                <label style={styles.label}>Company</label>
                <select
                  className="form-select form-select-sm"
                  style={styles.input}
                  value={form.companyId}
                  onChange={(e) =>
                    setForm({ ...form, companyId: e.target.value })
                  }
                >
                  <option value="">No company</option>
                  {companies.map((c) => (
                    <option key={c.id || c.Id} value={c.id || c.Id}>
                      {c.companyName || c.CompanyName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12 d-flex align-items-center gap-2 mt-1">
                <input
                  id="activeUser"
                  type="checkbox"
                  className="form-check-input mt-0"
                  checked={form.isActive}
                  onChange={(e) =>
                    setForm({ ...form, isActive: e.target.checked })
                  }
                />
                <label
                  htmlFor="activeUser"
                  style={{ fontSize: 11, color: "#495057" }}
                >
                  Active user
                </label>
              </div>

              <div className="col-12">
                <button
                  className="btn btn-primary btn-sm w-100"
                  style={styles.btn}
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Register User"}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="col-lg-8">
          <div style={styles.card}>
            <div
              style={{
                ...styles.header,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>
                <i className="bi bi-people me-2"></i>
                Users
              </span>
              <span style={{ fontSize: 10, opacity: 0.85 }}>
                Role based access
              </span>
            </div>

            <div style={{ overflowX: "auto" }}>
              <table
                className="table table-sm table-hover align-middle"
                style={styles.table}
              >
                <thead>
                  <tr>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Email</th>
                    <th style={styles.th}>Company</th>
                    <th style={styles.th}>Role</th>
                    <th style={styles.th}>Status</th>
                  </tr>
                </thead>

                <tbody>
                  {users.length ? (
                    users.map((u, i) => (
                      <tr key={u.id || u.Id || i}>
                        <td style={styles.td}>{u.fullName || u.FullName}</td>
                        <td style={styles.td}>{u.email || u.Email}</td>
                        <td style={styles.td}>
                          {u.companyName || u.CompanyName || "-"}
                        </td>
                        <td style={styles.td}>
                          <span
                            className="badge text-bg-primary"
                            style={styles.badge}
                          >
                            {u.roleName || u.RoleName}
                          </span>
                        </td>
                        <td style={styles.td}>
                          {(u.isActive ?? u.IsActive) ? (
                            <span
                              className="badge text-bg-success"
                              style={styles.badge}
                            >
                              Active
                            </span>
                          ) : (
                            <span
                              className="badge text-bg-secondary"
                              style={styles.badge}
                            >
                              Inactive
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="5"
                        className="text-center text-muted"
                        style={{ ...styles.td, padding: 20 }}
                      >
                        No users found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
